import { useState } from 'react'
import { Button, Input, Select, Typography, Row, Col } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { saveEditArenaGame } from 'src/api/arena'
import type { ArenaGameEditPayload } from 'src/api/arena'
import PrivateImage from 'src/components/PrivateImage'
import PrivateVideo from 'src/components/PrivateVideo'
import './EditArenaGame.css'

const { Title, Text } = Typography

type ImageSlotKey =
  | 'thumbnail'
  | 'inner'
  | 'dailyChallenge'
  | 'bannerHolder'
  | 'maintenance'
  | 'comingSoon'
  | 'splash'

interface MediaState {
  
  preview: string | null
  
  file: File | null
}

const IMAGE_SLOTS: { key: ImageSlotKey; title: string }[] = [
  { key: 'thumbnail', title: 'Thumbnail' },
  { key: 'inner', title: 'Inner Game Banner' },
  { key: 'dailyChallenge', title: 'Daily Challenge Banner' },
  { key: 'bannerHolder', title: 'Game Banner Holder' },
  { key: 'maintenance', title: 'Maintenance' },
  { key: 'comingSoon', title: 'Coming Soon' },
  { key: 'splash', title: 'Splash Screen' },
]

const IMAGE_ACCEPT = '.webp,image/webp'
const VIDEO_ACCEPT = '.mp4,video/mp4'

const STATUS_OPTIONS: { label: string; value: ArenaGameStatus }[] = [
  { label: 'Active', value: 'ACTIVE' },
  { label: 'Temporary Unavailable', value: 'TEMPORARY_UNAVAILABLE' },
  { label: 'Coming Soon', value: 'COMING_SOON' },
]

const isLocalPreview = (preview: string | null): boolean =>
  !!preview && preview.startsWith('blob:')

const EditArenaGame = (): JSX.Element => {
  const { id = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const params = new URLSearchParams(location.search)
  const title = params.get('title') ?? ''

  const [status, setStatus] = useState<ArenaGameStatus>(
    (params.get('status') as ArenaGameStatus) || 'ACTIVE'
  )

  const [images, setImages] = useState<Record<ImageSlotKey, MediaState>>({
    thumbnail: { preview: params.get('thumbnail'), file: null },
    inner: { preview: params.get('inner'), file: null },
    dailyChallenge: { preview: params.get('dailyChallenge'), file: null },
    bannerHolder: { preview: params.get('bannerHolder'), file: null },
    maintenance: { preview: params.get('maintenance'), file: null },
    comingSoon: { preview: params.get('comingSoon'), file: null },
    splash: { preview: params.get('splash'), file: null },
  })
  const [video, setVideo] = useState<MediaState>({
    preview: params.get('video'),
    file: null,
  })
  const [saving, setSaving] = useState<boolean>(false)

  const handleImageUpload = (
    key: ImageSlotKey,
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    setImages((prev) => ({ ...prev, [key]: { preview, file } }))
  }

  const handleVideoUpload = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const file = event.target.files?.[0]
    if (!file) return
    const preview = URL.createObjectURL(file)
    setVideo({ preview, file })
  }

  const handleSave = async (): Promise<void> => {
    setSaving(true)
    const payload: ArenaGameEditPayload = {
      gameId: id,
      status,
      thumbnailImage: images.thumbnail.file,
      innerBannerImage: images.inner.file,
      dailyChallengeImage: images.dailyChallenge.file,
      bannerHolderImage: images.bannerHolder.file,
      maintenanceImage: images.maintenance.file,
      comingSoonImage: images.comingSoon.file,
      splashImage: images.splash.file,
      video: video.file,
    }

    const result = await saveEditArenaGame(payload)
    setSaving(false)
    if (result === 'success') {
      navigate('/arena/games')
    }
  }

  const renderUploadBox = (key: ImageSlotKey): JSX.Element => {
    const state = images[key]
    return (
      <div
        className="arena-upload-box"
        onClick={() => document.getElementById(`upload-${key}`)?.click()}
      >
        {state.preview ? (
          isLocalPreview(state.preview) ? (
            <img src={state.preview} alt={key} />
          ) : (
            <PrivateImage src={state.preview} width="100%" height={118} />
          )
        ) : (
          <>
            <PlusOutlined />
            <div className="arena-upload-title">Upload</div>
            <div className="arena-upload-hint">(webp format only)</div>
          </>
        )}
        <input
          id={`upload-${key}`}
          type="file"
          hidden
          accept={IMAGE_ACCEPT}
          onChange={(e) => handleImageUpload(key, e)}
        />
      </div>
    )
  }

  return (
    <div className="edit-arena-container">
      <Title level={3}>
        <Text>Arena Games</Text>
      </Title>

      <div className="arena-section-label">Game Name</div>
      <div className="arena-name-row">
        
        <Input value={title} readOnly />
      </div>

      <Row gutter={[24, 24]}>
        {IMAGE_SLOTS.map((slot) => (
          <Col key={slot.key} xs={24} sm={12} md={8}>
            <div className="arena-section-label">{slot.title}</div>
            {renderUploadBox(slot.key)}
          </Col>
        ))}
        <Col xs={24} sm={12} md={8}>
          <div className="arena-section-label">Game Play</div>
          <div className="arena-video-card">
            {video.preview ? (
              <PrivateVideo
                src={video.preview}
                className="arena-video-card-player"
              />
            ) : (
              <div className="arena-upload-hint">No video</div>
            )}
          </div>
        </Col>
      </Row>

      <div className="arena-play-status-row">
        <div className="arena-play-col">
          <div className="arena-section-label">Game Play</div>
          <div className="arena-video-row">
            <Input
              value={video.file ? video.file.name : ''}
              placeholder="Upload mp4 format only"
              readOnly
            />
            <Button
              type="primary" className="btn-primary" style={{ width: 150 }}
              onClick={() => document.getElementById('upload-video')?.click()}
            >
              upload Video
            </Button>
            <input
              id="upload-video"
              type="file"
              hidden
              accept={VIDEO_ACCEPT}
              onChange={handleVideoUpload}
            />
          </div>
        </div>
        <div className="arena-status-col">
          <div className="arena-section-label">Status</div>
          <Select
            className="search-input"
            value={status}
            options={STATUS_OPTIONS}
            onChange={setStatus}
          />
        </div>
      </div>
      {video.preview && isLocalPreview(video.preview) && (
        <video className="arena-video-preview" src={video.preview} controls />
      )}

      <div style={{ marginTop: 28 }}>
        <Button type="primary" className="btn-primary" style={{ width: 200 }} loading={saving} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  )
}

EditArenaGame.displayName = 'EditArenaGame'

export default EditArenaGame
